import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Brain, Sparkles, Users, BarChart, Shield, Clock, Globe, Check, Award, Zap, ChevronDown, Calendar, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { contactApi } from '../api/contact';

const Landing = () => {
  const { loading } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  
  // Contact form states
  const [activeForm, setActiveForm] = useState<'sales' | 'demo' | null>(null);
  
  // Sales form states
  const [salesFormData, setSalesFormData] = useState({
    full_name: '',
    email: '',
    company: '',
    number_of_employees: '',
    message: ''
  });
  const [salesFormSubmitting, setSalesFormSubmitting] = useState(false);
  const [salesFormSuccess, setSalesFormSuccess] = useState(false);
  const [salesFormError, setSalesFormError] = useState('');
  
  // Demo form states
  const [demoFormData, setDemoFormData] = useState({
    full_name: '',
    email: '',
    company: '',
    position: '',
    preferred_date: '',
    preferred_time: ''
  });
  const [demoFormSubmitting, setDemoFormSubmitting] = useState(false);
  const [demoFormSuccess, setDemoFormSuccess] = useState(false);
  const [demoFormError, setDemoFormError] = useState('');
  
  // Close dropdown when clicking outside
  const languageDropdownRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setIsLanguageDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Form input change handlers
  const handleSalesFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setSalesFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleDemoFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setDemoFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Form submission handlers
  const handleSalesFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset states
    setSalesFormError('');
    setSalesFormSubmitting(true);
    setSalesFormSuccess(false);
    
    try {
      // Map form data to API request format
      const requestData = {
        full_name: salesFormData.full_name,
        email: salesFormData.email,
        company: salesFormData.company,
        number_of_employees: salesFormData.number_of_employees,
        message: salesFormData.message
      };
      
      // Send the request to the API
      await contactApi.messageSales(requestData);
      
      // Show success message and reset form
      setSalesFormSuccess(true);
      setSalesFormData({
        full_name: '',
        email: '',
        company: '',
        number_of_employees: '',
        message: ''
      });
      
      // Automatically close the form after 3 seconds on success
      setTimeout(() => {
        if (salesFormSuccess) {
          setActiveForm(null);
        }
      }, 3000);
      
    } catch (error) {
      console.error('Failed to submit contact form:', error);
      setSalesFormError(t('landing.contact.salesForm.submitError') || 'Failed to send message. Please try again.');
    } finally {
      setSalesFormSubmitting(false);
    }
  };
  
  const handleDemoFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset states
    setDemoFormError('');
    setDemoFormSubmitting(true);
    setDemoFormSuccess(false);
    
    try {
      // Map form data to API request format
      const requestData = {
        full_name: demoFormData.full_name,
        email: demoFormData.email,
        company: demoFormData.company,
        position: demoFormData.position,
        preferred_date: demoFormData.preferred_date,
        preferred_time: demoFormData.preferred_time
      };
      
      // Send the request to the API
      await contactApi.scheduleDemo(requestData);
      
      // Show success message and reset form
      setDemoFormSuccess(true);
      setDemoFormData({
        full_name: '',
        email: '',
        company: '',
        position: '',
        preferred_date: '',
        preferred_time: ''
      });
      
      // Automatically close the form after 3 seconds on success
      setTimeout(() => {
        if (demoFormSuccess) {
          setActiveForm(null);
        }
      }, 3000);
      
    } catch (error) {
      console.error('Failed to submit demo form:', error);
      setDemoFormError(t('landing.contact.demoForm.submitError') || 'Failed to schedule demo. Please try again.');
    } finally {
      setDemoFormSubmitting(false);
    }
  };

  // Show loading spinner if authentication is being checked
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
    </div>;
  }
  
  // If we get here, user is not authenticated, so show landing page
  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#1F3B4D]/90 backdrop-blur-xl border-b border-[#1F3B4D]/10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center py-2">
              <a href="#" className="block py-2">
                <img 
                  src="/images/Branco-Horizontal.svg" 
                  alt="Aura" 
                  className="h-10 sm:h-12 w-auto" 
                />
              </a>
            </div>
            <div className="hidden md:flex items-center gap-10">
              <a href="#features" className="text-sm font-medium text-[#F4F5F7] hover:text-white hover:underline decoration-[#4F94D4] decoration-2 underline-offset-8 transition-colors">
                {t('landing.navigation.features')}
              </a>
              <a href="#how-it-works" className="text-sm font-medium text-[#F4F5F7] hover:text-white hover:underline decoration-[#4F94D4] decoration-2 underline-offset-8 transition-colors">
                {t('landing.navigation.howItWorks')}
              </a>
              <a href="#ai" className="text-sm font-medium text-[#F4F5F7] hover:text-white hover:underline decoration-[#4F94D4] decoration-2 underline-offset-8 transition-colors">
                {t('landing.navigation.aiAnalysis')}
              </a>
              <a href="#faq" className="text-sm font-medium text-[#F4F5F7] hover:text-white hover:underline decoration-[#4F94D4] decoration-2 underline-offset-8 transition-colors">
                {t('landing.navigation.faq')}
              </a>
              <a href="#contact" className="text-sm font-medium text-[#F4F5F7] hover:text-white hover:underline decoration-[#4F94D4] decoration-2 underline-offset-8 transition-colors">
                {t('landing.navigation.contact')}
              </a>
            </div>
            <div className="flex items-center gap-5">
              {/* Language Switcher */}
              <div className="relative" ref={languageDropdownRef}>
                <button 
                  onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                  className="flex items-center gap-1.5 py-2 px-3 rounded-full text-[#F4F5F7] hover:text-white hover:bg-[#1F3B4D]/50 transition-colors border border-transparent hover:border-[#4F94D4]/30"
                  aria-label="Switch language"
                  aria-expanded={isLanguageDropdownOpen}
                  aria-haspopup="true"
                >
                  <Globe className="w-4 h-4" />
                  <span className="text-sm font-medium">{language === 'en' ? 'EN' : 'PT'}</span>
                  <svg 
                    className={`w-3 h-3 opacity-70 transition-transform duration-200 ${isLanguageDropdownOpen ? 'rotate-180' : ''}`} 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
                
                {/* Dropdown */}
                {isLanguageDropdownOpen && (
                  <div 
                    className="absolute right-0 top-full mt-2 w-36 bg-[#1F3B4D] rounded-lg shadow-xl border border-[#4F94D4]/20 py-2 z-50 animate-fadeIn"
                    role="menu"
                  >
                    <button
                      onClick={() => {
                        setLanguage('en');
                        setIsLanguageDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-2.5 text-sm text-left flex items-center gap-2 ${language === 'en' ? 'bg-[#4F94D4]/20 font-medium text-white' : 'hover:bg-[#1F3B4D]/80 text-[#F4F5F7]'}`}
                      role="menuitem"
                    >
                      <span className="text-base">🇺🇸</span>
                      <span>English</span>
                      {language === 'en' && (
                        <svg className="w-4 h-4 ml-auto" viewBox="0 0 24 24" fill="none">
                          <path d="M5 12L10 17L20 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setLanguage('pt');
                        setIsLanguageDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-2.5 text-sm text-left flex items-center gap-2 ${language === 'pt' ? 'bg-[#4F94D4]/20 font-medium text-white' : 'hover:bg-[#1F3B4D]/80 text-[#F4F5F7]'}`}
                      role="menuitem"
                    >
                      <span className="text-base">🇧🇷</span>
                      <span>Português</span>
                      {language === 'pt' && (
                        <svg className="w-4 h-4 ml-auto" viewBox="0 0 24 24" fill="none">
                          <path d="M5 12L10 17L20 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </button>
                  </div>
                )}
              </div>
              
              <Link 
                to="/signin" 
                className="text-sm font-medium text-[#F4F5F7] hover:text-white transition-colors px-3 py-2"
              >
                {t('landing.navigation.signIn')}
              </Link>
              <button
                onClick={() => setActiveForm('sales')}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-[#1F3B4D] bg-[#FFC857] rounded-full hover:bg-[#FFC857]/90 transition-all shadow-sm hover:shadow"
              >
                {t('landing.navigation.contactSales')}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative pt-36 pb-24 sm:pt-48 sm:pb-32" id="features">
        <div className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80">
          <div className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#1F3B4D] via-[#4F94D4] to-[#FFC857] opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]" />
        </div>

        <div className="mx-auto max-w-full px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl lg:max-w-6xl xl:max-w-7xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-[#1F3B4D] sm:text-5xl md:text-6xl lg:text-6xl xl:leading-tight">
              {language === 'en' ? (
                <>
                  Anticipate tomorrow with{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1F3B4D] via-[#4F94D4] to-[#FFC857]">
                    AI-powered insights from the past
                  </span>
                </>
              ) : (
                <>
                  Antecipe o amanhã com{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1F3B4D] via-[#4F94D4] to-[#FFC857]">
                    insights do passado impulsionados por IA
                  </span>
                </>
              )}
            </h1>
            <p className="mt-8 text-xl leading-8 text-[#1F3B4D]/80 max-w-2xl mx-auto">
              {t('landing.hero.description')}
            </p>
            <div className="mt-12 flex items-center justify-center gap-x-6">
              <button
                onClick={() => setActiveForm('demo')}
                className="inline-flex items-center gap-2 px-8 py-3.5 text-base font-semibold text-white bg-[#4F94D4] rounded-full hover:bg-[#4F94D4]/90 transition-all shadow-md hover:shadow-lg"
              >
                {t('landing.hero.scheduleDemo')}
                <ArrowRight className="w-4 h-4 ml-1" />
              </button>
              <a 
                href="#features" 
                className="text-base font-semibold leading-6 text-[#1F3B4D] hover:text-[#4F94D4] transition-colors flex items-center"
              >
                {t('landing.hero.learnMore')} 
                <svg className="w-4 h-4 ml-1 group-hover:ml-2 transition-all" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>

          {/* Feature Cards */}
          <div className="mt-24 max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-10">
              <div className="relative group">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#1F3B4D]/5 to-[#4F94D4]/5 transform -rotate-1 group-hover:rotate-0 transition-transform"></div>
                <div className="relative rounded-xl bg-white p-6 md:p-6 lg:p-8 shadow-md hover:shadow-lg transition-shadow duration-300 border border-[#1F3B4D]/10 h-full flex flex-col">
                  <div className="inline-flex items-center justify-center p-3 bg-[#4F94D4]/10 rounded-xl mb-4 self-start">
                    <BarChart className="h-6 w-6 text-[#4F94D4]" />
                  </div>
                  <h3 className="text-lg lg:text-xl font-semibold mb-3 text-[#1F3B4D]">{t('landing.features.advancedAnalytics.title')}</h3>
                  <p className="text-[#1F3B4D]/70 leading-relaxed text-sm lg:text-base">
                    {t('landing.features.advancedAnalytics.description')}
                  </p>
                </div>
              </div>

              <div className="relative group">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#4F94D4]/5 to-[#FFC857]/5 transform -rotate-1 group-hover:rotate-0 transition-transform"></div>
                <div className="relative rounded-xl bg-white p-6 md:p-6 lg:p-8 shadow-md hover:shadow-lg transition-shadow duration-300 border border-[#1F3B4D]/10 h-full flex flex-col">
                  <div className="inline-flex items-center justify-center p-3 bg-[#FFC857]/10 rounded-xl mb-4 self-start">
                    <Brain className="h-6 w-6 text-[#FFC857]" />
                  </div>
                  <h3 className="text-lg lg:text-xl font-semibold mb-3 text-[#1F3B4D]">{t('landing.features.aiInsights.title')}</h3>
                  <p className="text-[#1F3B4D]/70 leading-relaxed text-sm lg:text-base">
                    {t('landing.features.aiInsights.description')}
                  </p>
                </div>
              </div>

              <div className="relative group">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#FFC857]/5 to-[#1F3B4D]/5 transform -rotate-1 group-hover:rotate-0 transition-transform"></div>
                <div className="relative rounded-xl bg-white p-6 md:p-6 lg:p-8 shadow-md hover:shadow-lg transition-shadow duration-300 border border-[#1F3B4D]/10 h-full flex flex-col">
                  <div className="inline-flex items-center justify-center p-3 bg-[#1F3B4D]/10 rounded-xl mb-4 self-start">
                    <Shield className="h-6 w-6 text-[#1F3B4D]" />
                  </div>
                  <h3 className="text-lg lg:text-xl font-semibold mb-3 text-[#1F3B4D]">{t('landing.features.enterpriseSecurity.title')}</h3>
                  <p className="text-[#1F3B4D]/70 leading-relaxed text-sm lg:text-base">
                    {t('landing.features.enterpriseSecurity.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="py-28 sm:py-32 bg-white" id="how-it-works">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center">
            <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-[#1F3B4D]/10 text-[#1F3B4D] font-medium text-sm mb-4">
              Simple & Effective
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-[#1F3B4D] sm:text-5xl mb-8">
              {t('landing.howItWorks.title')}
            </h2>
            <p className="text-xl leading-8 text-[#1F3B4D]/70 max-w-3xl mx-auto">
              {t('landing.howItWorks.subtitle')}
            </p>
          </div>

          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
            <div className="grid max-w-5xl mx-auto grid-cols-1 gap-10 md:grid-cols-4 md:gap-8">
              {/* Step 1 */}
              <div className="relative flex flex-col items-center p-6 lg:p-7 rounded-xl bg-white shadow-md hover:shadow-lg transition-shadow duration-300 border border-[#1F3B4D]/10 group h-full">
                <div className="absolute -top-6 rounded-full bg-[#4F94D4] w-12 h-12 flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-300">
                  <span className="text-white text-xl font-bold">1</span>
                </div>
                <dt className="mt-8 text-lg lg:text-xl font-semibold text-[#1F3B4D] text-center">{t('landing.howItWorks.steps.createJob.title')}</dt>
                <div className="w-12 h-1 bg-[#4F94D4] rounded my-3"></div>
                <dd className="text-[#1F3B4D]/70 leading-relaxed text-sm lg:text-base text-center">
                  {t('landing.howItWorks.steps.createJob.description')}
                </dd>
              </div>
              
              {/* Step 2 */}
              <div className="relative flex flex-col items-center p-6 lg:p-7 rounded-xl bg-white shadow-md hover:shadow-lg transition-shadow duration-300 border border-[#1F3B4D]/10 group h-full">
                <div className="absolute -top-6 rounded-full bg-[#4F94D4] w-12 h-12 flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-300">
                  <span className="text-white text-xl font-bold">2</span>
                </div>
                <dt className="mt-8 text-lg lg:text-xl font-semibold text-[#1F3B4D] text-center">{t('landing.howItWorks.steps.addCandidates.title')}</dt>
                <div className="w-12 h-1 bg-[#4F94D4] rounded my-3"></div>
                <dd className="text-[#1F3B4D]/70 leading-relaxed text-sm lg:text-base text-center">
                  {t('landing.howItWorks.steps.addCandidates.description')}
                </dd>
              </div>
              
              {/* Step 3 */}
              <div className="relative flex flex-col items-center p-6 lg:p-7 rounded-xl bg-white shadow-md hover:shadow-lg transition-shadow duration-300 border border-[#1F3B4D]/10 group h-full">
                <div className="absolute -top-6 rounded-full bg-[#4F94D4] w-12 h-12 flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-300">
                  <span className="text-white text-xl font-bold">3</span>
                </div>
                <dt className="mt-8 text-lg lg:text-xl font-semibold text-[#1F3B4D] text-center">{t('landing.howItWorks.steps.automatedChecks.title')}</dt>
                <div className="w-12 h-1 bg-[#4F94D4] rounded my-3"></div>
                <dd className="text-[#1F3B4D]/70 leading-relaxed text-sm lg:text-base text-center">
                  {t('landing.howItWorks.steps.automatedChecks.description')}
                </dd>
              </div>
              
              {/* Step 4 */}
              <div className="relative flex flex-col items-center p-6 lg:p-7 rounded-xl bg-white shadow-md hover:shadow-lg transition-shadow duration-300 border border-[#1F3B4D]/10 group h-full">
                <div className="absolute -top-6 rounded-full bg-[#4F94D4] w-12 h-12 flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-300">
                  <span className="text-white text-xl font-bold">4</span>
                </div>
                <dt className="mt-8 text-lg lg:text-xl font-semibold text-[#1F3B4D] text-center">{t('landing.howItWorks.steps.aiAnalysis.title')}</dt>
                <div className="w-12 h-1 bg-[#4F94D4] rounded my-3"></div>
                <dd className="text-[#1F3B4D]/70 leading-relaxed text-sm lg:text-base text-center">
                  {t('landing.howItWorks.steps.aiAnalysis.description')}
                </dd>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Benefits Section */}
      <div className="py-28 sm:py-36 bg-gradient-to-b from-[#F4F5F7] to-white" id="ai">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center">
            <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-[#4F94D4]/10 text-[#4F94D4] font-medium text-sm mb-4">
              {t('landing.benefits.forHrLeaders')}
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-[#1F3B4D] sm:text-5xl mb-8">
              {t('landing.benefits.everythingYouNeed')}
            </h2>
            <p className="text-xl leading-8 text-[#1F3B4D]/70 max-w-3xl mx-auto">
              {t('landing.benefits.description')}
            </p>
          </div>

          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
            <dl className="grid max-w-5xl mx-auto grid-cols-1 gap-10 md:grid-cols-3 md:gap-6 lg:gap-8">
              <div className="relative flex flex-col items-start p-6 lg:p-7 rounded-xl bg-white shadow-md hover:shadow-lg transition-shadow duration-300 border border-[#1F3B4D]/10 group h-full">
                <div className="absolute -top-6 left-6 rounded-xl bg-[#4F94D4] p-3 shadow-md group-hover:scale-105 transition-transform duration-300">
                  <Clock className="h-5 w-5 text-white" />
                </div>
                <dt className="mt-8 text-lg lg:text-xl font-semibold text-[#1F3B4D]">{t('landing.benefits.saveTime.title')}</dt>
                <div className="w-12 h-1 bg-[#4F94D4] rounded my-3"></div>
                <dd className="text-[#1F3B4D]/70 leading-relaxed text-sm lg:text-base">
                  {t('landing.benefits.saveTime.description')}
                </dd>
              </div>
              
              <div className="relative flex flex-col items-start p-6 lg:p-7 rounded-xl bg-white shadow-md hover:shadow-lg transition-shadow duration-300 border border-[#1F3B4D]/10 group h-full">
                <div className="absolute -top-6 left-6 rounded-xl bg-[#FFC857] p-3 shadow-md group-hover:scale-105 transition-transform duration-300">
                  <Users className="h-5 w-5 text-[#1F3B4D]" />
                </div>
                <dt className="mt-8 text-lg lg:text-xl font-semibold text-[#1F3B4D]">{t('landing.benefits.betterInsights.title')}</dt>
                <div className="w-12 h-1 bg-[#FFC857] rounded my-3"></div>
                <dd className="text-[#1F3B4D]/70 leading-relaxed text-sm lg:text-base">
                  {t('landing.benefits.betterInsights.description')}
                </dd>
              </div>
              
              <div className="relative flex flex-col items-start p-6 lg:p-7 rounded-xl bg-white shadow-md hover:shadow-lg transition-shadow duration-300 border border-[#1F3B4D]/10 group h-full">
                <div className="absolute -top-6 left-6 rounded-xl bg-[#1F3B4D] p-3 shadow-md group-hover:scale-105 transition-transform duration-300">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <dt className="mt-8 text-lg lg:text-xl font-semibold text-[#1F3B4D]">{t('landing.benefits.reduceRisk.title')}</dt>
                <div className="w-12 h-1 bg-[#1F3B4D] rounded my-3"></div>
                <dd className="text-[#1F3B4D]/70 leading-relaxed text-sm lg:text-base">
                  {t('landing.benefits.reduceRisk.description')}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>


      {/* FAQ Section */}
      <div id="faq" className="py-28 sm:py-32 bg-[#F4F5F7]">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center mb-16">
            <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-[#1F3B4D]/10 text-[#1F3B4D] font-medium text-sm mb-4">
              Common Questions
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-[#1F3B4D] sm:text-5xl">
              {t('landing.faq.title')}
            </h2>
            <p className="mt-6 text-xl leading-8 text-[#1F3B4D]/70">
              {t('landing.faq.subtitle')}
            </p>
          </div>

          <div className="space-y-4">
            {/* FAQ Item 1 */}
            <div className="border border-[#1F3B4D]/10 rounded-xl overflow-hidden bg-white">
              <details className="group">
                <summary className="flex items-center justify-between cursor-pointer p-6">
                  <h3 className="text-lg font-semibold text-[#1F3B4D]">{t('landing.faq.items.aiAnalysis.question')}</h3>
                  <ChevronDown className="w-5 h-5 text-[#4F94D4] group-open:rotate-180 transition-transform" />
                </summary>
                <div className="p-6 pt-0 text-[#1F3B4D]/70">
                  <p>{t('landing.faq.items.aiAnalysis.answer')}</p>
                </div>
              </details>
            </div>
            
            {/* FAQ Item 2 */}
            <div className="border border-[#1F3B4D]/10 rounded-xl overflow-hidden bg-white">
              <details className="group">
                <summary className="flex items-center justify-between cursor-pointer p-6">
                  <h3 className="text-lg font-semibold text-[#1F3B4D]">{t('landing.faq.items.timelines.question')}</h3>
                  <ChevronDown className="w-5 h-5 text-[#4F94D4] group-open:rotate-180 transition-transform" />
                </summary>
                <div className="p-6 pt-0 text-[#1F3B4D]/70">
                  <p>{t('landing.faq.items.timelines.answer')}</p>
                </div>
              </details>
            </div>
            
            {/* FAQ Item 3 */}
            <div className="border border-[#1F3B4D]/10 rounded-xl overflow-hidden bg-white">
              <details className="group">
                <summary className="flex items-center justify-between cursor-pointer p-6">
                  <h3 className="text-lg font-semibold text-[#1F3B4D]">{t('landing.faq.items.compliance.question')}</h3>
                  <ChevronDown className="w-5 h-5 text-[#4F94D4] group-open:rotate-180 transition-transform" />
                </summary>
                <div className="p-6 pt-0 text-[#1F3B4D]/70">
                  <p>{t('landing.faq.items.compliance.answer')}</p>
                </div>
              </details>
            </div>
            
            {/* FAQ Item 4 */}
            <div className="border border-[#1F3B4D]/10 rounded-xl overflow-hidden bg-white">
              <details className="group">
                <summary className="flex items-center justify-between cursor-pointer p-6">
                  <h3 className="text-lg font-semibold text-[#1F3B4D]">{t('landing.faq.items.customization.question')}</h3>
                  <ChevronDown className="w-5 h-5 text-[#4F94D4] group-open:rotate-180 transition-transform" />
                </summary>
                <div className="p-6 pt-0 text-[#1F3B4D]/70">
                  <p>{t('landing.faq.items.customization.answer')}</p>
                </div>
              </details>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div id="contact" className="py-28 sm:py-36 bg-gradient-to-b from-white to-[#F4F5F7]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center mb-16">
            <h2 className="text-4xl font-bold tracking-tight text-[#1F3B4D] sm:text-5xl bg-clip-text text-transparent bg-gradient-to-r from-[#1F3B4D] via-[#4F94D4] to-[#FFC857]">
              {t('landing.contact.title')}
            </h2>
            <p className="mt-6 text-xl leading-8 text-[#1F3B4D]/70 max-w-2xl mx-auto">
              {t('landing.contact.description')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Fale com Vendas */}
            <div id="salesForm" className="bg-white rounded-2xl shadow-lg p-8 border border-[#1F3B4D]/10">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-xl bg-[#FFC857]/10">
                  <Users className="h-6 w-6 text-[#FFC857]" />
                </div>
                <h3 className="text-2xl font-bold text-[#1F3B4D]">{t('landing.contact.salesForm.title')}</h3>
              </div>
              <p className="text-[#1F3B4D]/70 mb-8">{t('landing.contact.salesForm.description')}</p>
              
              {/* Inline form in the contact section - use the same sales form handler */}
              {salesFormSuccess ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-4 text-center">
                  <div className="inline-flex justify-center items-center bg-green-100 rounded-full p-2 mb-4">
                    <Check className="h-8 w-8 text-green-600" />
                  </div>
                  <h4 className="text-xl font-semibold text-green-800 mb-2">{t('landing.contact.salesForm.submitSuccess') || 'Message sent successfully!'}</h4>
                  <p className="text-green-700">{t('landing.contact.salesForm.submitSuccessDetail') || 'Thank you for contacting us. Our team will get back to you shortly.'}</p>
                  <button 
                    onClick={() => {
                      setSalesFormSuccess(false);
                      setSalesFormError('');
                    }}
                    className="mt-6 px-6 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors"
                  >
                    {t('landing.contact.salesForm.submitSuccessButton') || 'Send another message'}
                  </button>
                </div>
              ) : (
                <form className="space-y-5" onSubmit={handleSalesFormSubmit}>
                  {salesFormError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-2 flex items-start gap-3">
                      <div className="text-red-500 mt-0.5">
                        <AlertCircle className="h-5 w-5" />
                      </div>
                      <p className="text-red-800">{salesFormError}</p>
                    </div>
                  )}
                  
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-[#1F3B4D] mb-2">
                      {t('landing.contact.salesForm.name')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="full_name"
                      value={salesFormData.full_name}
                      onChange={handleSalesFormChange}
                      className="w-full px-4 py-3 border border-[#1F3B4D]/20 rounded-lg focus:ring-2 focus:ring-[#4F94D4] focus:border-[#4F94D4] outline-none transition-all"
                      placeholder={t('landing.contact.salesForm.namePlaceholder')}
                      required
                      disabled={salesFormSubmitting}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-[#1F3B4D] mb-2">
                      {t('landing.contact.salesForm.email')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={salesFormData.email}
                      onChange={handleSalesFormChange}
                      className="w-full px-4 py-3 border border-[#1F3B4D]/20 rounded-lg focus:ring-2 focus:ring-[#4F94D4] focus:border-[#4F94D4] outline-none transition-all"
                      placeholder={t('landing.contact.salesForm.emailPlaceholder')}
                      required
                      disabled={salesFormSubmitting}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-[#1F3B4D] mb-2">
                      {t('landing.contact.salesForm.company')}
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={salesFormData.company}
                      onChange={handleSalesFormChange}
                      className="w-full px-4 py-3 border border-[#1F3B4D]/20 rounded-lg focus:ring-2 focus:ring-[#4F94D4] focus:border-[#4F94D4] outline-none transition-all"
                      placeholder={t('landing.contact.salesForm.companyPlaceholder')}
                      required
                      disabled={salesFormSubmitting}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="employees" className="block text-sm font-medium text-[#1F3B4D] mb-2">
                      {t('landing.contact.salesForm.employees')}
                    </label>
                    <select
                      id="employees"
                      name="number_of_employees"
                      value={salesFormData.number_of_employees}
                      onChange={handleSalesFormChange}
                      className="w-full px-4 py-3 border border-[#1F3B4D]/20 rounded-lg focus:ring-2 focus:ring-[#4F94D4] focus:border-[#4F94D4] outline-none transition-all"
                      required
                      disabled={salesFormSubmitting}
                    >
                      <option value="">{t('landing.contact.salesForm.employeesPlaceholder')}</option>
                      <option value="1-10">1-10</option>
                      <option value="11-50">11-50</option>
                      <option value="51-200">51-200</option>
                      <option value="201-500">201-500</option>
                      <option value="501+">501+</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-[#1F3B4D] mb-2">
                      {t('landing.contact.salesForm.message')}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={salesFormData.message}
                      onChange={handleSalesFormChange}
                      rows={6}
                      className="w-full px-4 py-3 border border-[#1F3B4D]/20 rounded-lg focus:ring-2 focus:ring-[#4F94D4] focus:border-[#4F94D4] outline-none transition-all"
                      placeholder={t('landing.contact.salesForm.messagePlaceholder')}
                      required
                      disabled={salesFormSubmitting}
                    ></textarea>
                  </div>
                  
                  <button
                    type="submit"
                    className={`w-full rounded-lg bg-[#FFC857] px-6 py-4 mt-2 text-base font-semibold text-[#1F3B4D] shadow-md hover:bg-[#FFC857]/90 hover:shadow-lg transition-all flex items-center justify-center ${salesFormSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                    disabled={salesFormSubmitting}
                  >
                    {salesFormSubmitting ? (
                      <>
                        <span className="w-5 h-5 border-2 border-[#1F3B4D] border-t-transparent rounded-full animate-spin mr-2"></span>
                        {t('landing.contact.salesForm.submitting') || 'Sending...'}
                      </>
                    ) : (
                      <>
                        {t('landing.contact.salesForm.submit')}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
            
            {/* Agendar Demonstração */}
            <div id="demoForm" className="bg-white rounded-2xl shadow-lg p-8 border border-[#1F3B4D]/10">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-xl bg-[#4F94D4]/10">
                  <Calendar className="h-6 w-6 text-[#4F94D4]" />
                </div>
                <h3 className="text-2xl font-bold text-[#1F3B4D]">{t('landing.contact.demoForm.title')}</h3>
              </div>
              <p className="text-[#1F3B4D]/70 mb-8">{t('landing.contact.demoForm.description')}</p>
              
              {/* Inline demo form - use the same demo form handler */}
              {demoFormSuccess ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-4 text-center">
                  <div className="inline-flex justify-center items-center bg-green-100 rounded-full p-2 mb-4">
                    <Check className="h-8 w-8 text-green-600" />
                  </div>
                  <h4 className="text-xl font-semibold text-green-800 mb-2">{t('landing.contact.demoForm.submitSuccess') || 'Demo scheduled successfully!'}</h4>
                  <p className="text-green-700">{t('landing.contact.demoForm.submitSuccessDetail') || 'Thank you for scheduling a demo. We will contact you shortly to confirm the details.'}</p>
                  <button 
                    onClick={() => {
                      setDemoFormSuccess(false);
                      setDemoFormError('');
                    }}
                    className="mt-6 px-6 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors"
                  >
                    {t('landing.contact.demoForm.submitSuccessButton') || 'Schedule another demo'}
                  </button>
                </div>
              ) : (
                <form className="space-y-5" onSubmit={handleDemoFormSubmit}>
                  {demoFormError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-2 flex items-start gap-3">
                      <div className="text-red-500 mt-0.5">
                        <AlertCircle className="h-5 w-5" />
                      </div>
                      <p className="text-red-800">{demoFormError}</p>
                    </div>
                  )}
                
                  <div>
                    <label htmlFor="demo-name" className="block text-sm font-medium text-[#1F3B4D] mb-2">
                      {t('landing.contact.demoForm.name')}
                    </label>
                    <input
                      type="text"
                      id="demo-name"
                      name="full_name"
                      value={demoFormData.full_name}
                      onChange={handleDemoFormChange}
                      className="w-full px-4 py-3 border border-[#1F3B4D]/20 rounded-lg focus:ring-2 focus:ring-[#4F94D4] focus:border-[#4F94D4] outline-none transition-all"
                      placeholder={t('landing.contact.demoForm.namePlaceholder')}
                      required
                      disabled={demoFormSubmitting}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="demo-email" className="block text-sm font-medium text-[#1F3B4D] mb-2">
                      {t('landing.contact.demoForm.email')}
                    </label>
                    <input
                      type="email"
                      id="demo-email"
                      name="email"
                      value={demoFormData.email}
                      onChange={handleDemoFormChange}
                      className="w-full px-4 py-3 border border-[#1F3B4D]/20 rounded-lg focus:ring-2 focus:ring-[#4F94D4] focus:border-[#4F94D4] outline-none transition-all"
                      placeholder={t('landing.contact.demoForm.emailPlaceholder')}
                      required
                      disabled={demoFormSubmitting}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="demo-company" className="block text-sm font-medium text-[#1F3B4D] mb-2">
                      {t('landing.contact.demoForm.company')}
                    </label>
                    <input
                      type="text"
                      id="demo-company"
                      name="company"
                      value={demoFormData.company}
                      onChange={handleDemoFormChange}
                      className="w-full px-4 py-3 border border-[#1F3B4D]/20 rounded-lg focus:ring-2 focus:ring-[#4F94D4] focus:border-[#4F94D4] outline-none transition-all"
                      placeholder={t('landing.contact.demoForm.companyPlaceholder')}
                      required
                      disabled={demoFormSubmitting}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="position" className="block text-sm font-medium text-[#1F3B4D] mb-2">
                      {t('landing.contact.demoForm.position')}
                    </label>
                    <input
                      type="text"
                      id="position"
                      name="position"
                      value={demoFormData.position}
                      onChange={handleDemoFormChange}
                      className="w-full px-4 py-3 border border-[#1F3B4D]/20 rounded-lg focus:ring-2 focus:ring-[#4F94D4] focus:border-[#4F94D4] outline-none transition-all"
                      placeholder={t('landing.contact.demoForm.positionPlaceholder')}
                      required
                      disabled={demoFormSubmitting}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="preferred-date" className="block text-sm font-medium text-[#1F3B4D] mb-2">
                      {t('landing.contact.demoForm.preferredDate')}
                    </label>
                    <input
                      type="date"
                      id="preferred-date"
                      name="preferred_date"
                      value={demoFormData.preferred_date}
                      onChange={handleDemoFormChange}
                      className="w-full px-4 py-3 border border-[#1F3B4D]/20 rounded-lg focus:ring-2 focus:ring-[#4F94D4] focus:border-[#4F94D4] outline-none transition-all"
                      required
                      disabled={demoFormSubmitting}
                      min={new Date().toISOString().split('T')[0]} // Set minimum date to today
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="preferred-time" className="block text-sm font-medium text-[#1F3B4D] mb-2">
                      {t('landing.contact.demoForm.preferredTime')}
                    </label>
                    <select
                      id="preferred-time"
                      name="preferred_time"
                      value={demoFormData.preferred_time}
                      onChange={handleDemoFormChange}
                      className="w-full px-4 py-3 border border-[#1F3B4D]/20 rounded-lg focus:ring-2 focus:ring-[#4F94D4] focus:border-[#4F94D4] outline-none transition-all"
                      required
                      disabled={demoFormSubmitting}
                    >
                      <option value="">{t('landing.contact.demoForm.preferredTimePlaceholder')}</option>
                      <option value="9am-12pm">{t('landing.contact.demoForm.morning')}</option>
                      <option value="12pm-5pm">{t('landing.contact.demoForm.afternoon')}</option>
                      <option value="5pm-8pm">{t('landing.contact.demoForm.evening')}</option>
                    </select>
                  </div>
                  
                  <button
                    type="submit"
                    className={`w-full rounded-lg bg-[#4F94D4] px-6 py-4 mt-2 text-base font-semibold text-white shadow-md hover:bg-[#4F94D4]/90 hover:shadow-lg transition-all flex items-center justify-center ${demoFormSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                    disabled={demoFormSubmitting}
                  >
                    {demoFormSubmitting ? (
                      <>
                        <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                        {t('landing.contact.demoForm.submitting') || 'Scheduling...'}
                      </>
                    ) : (
                      <>
                        {t('landing.contact.demoForm.submit')}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1F3B4D] pt-16 pb-12 border-t border-[#1F3B4D]/80">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between gap-12 mb-12">
            <div>
              <div className="flex items-center mb-6">
                <img 
                  src="/images/Branco-Horizontal.svg" 
                  alt="Aura" 
                  className="h-10 w-auto" 
                  loading="lazy"
                />
              </div>
              <p className="text-[#F4F5F7]/80 text-sm max-w-md mb-8 leading-relaxed">
                AI-powered reference checking platform that helps HR leaders make better hiring decisions with comprehensive candidate assessments backed by data.
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setLanguage(language === 'en' ? 'pt' : 'en')}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#1F3B4D]/60 text-[#F4F5F7] hover:bg-[#1F3B4D]/40 transition-colors text-sm border border-[#4F94D4]/30"
                >
                  <Globe className="w-4 h-4" />
                  <span>{language === 'en' ? 'English' : 'Português'}</span>
                  <span className="flex items-center gap-1 text-[#F4F5F7]/50">
                    <span>|</span>
                    <span className="text-xs">{language === 'en' ? 'PT' : 'EN'}</span>
                  </span>
                </button>
              </div>
            </div>
            
            <div>
              <h3 className="text-white font-semibold mb-4">{t('landing.footer.quickLinks')}</h3>
              <ul className="space-y-3">
                <li><a href="#features" className="text-[#F4F5F7]/80 hover:text-white transition-colors text-sm">{t('landing.navigation.features')}</a></li>
                <li><a href="#how-it-works" className="text-[#F4F5F7]/80 hover:text-white transition-colors text-sm">{t('landing.navigation.howItWorks')}</a></li>
                <li><a href="#ai" className="text-[#F4F5F7]/80 hover:text-white transition-colors text-sm">{t('landing.navigation.aiAnalysis')}</a></li>
                <li><a href="#faq" className="text-[#F4F5F7]/80 hover:text-white transition-colors text-sm">{t('landing.navigation.faq')}</a></li>
                <li><a href="#contact" className="text-[#F4F5F7]/80 hover:text-white transition-colors text-sm">{t('landing.navigation.contact')}</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-[#4F94D4]/20 pt-8">
            <p className="text-center text-[#F4F5F7]/50 text-sm">
              {t('landing.footer.copyright').replace('{year}', new Date().getFullYear().toString())}
            </p>
            <p className="text-center text-[#F4F5F7]/50 text-sm">CNPJ: 59.506.936/0001-31</p>
          </div>
        </div>
      </footer>

      {/* Contact Form Section - Visible only when a form is selected */}
      {activeForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-fadeIn">
            {/* Sales Form */}
            {activeForm === 'sales' && (
              <>
                <div className="p-6 pb-2 flex items-center justify-between border-b border-[#1F3B4D]/10">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-[#FFC857]/10">
                      <Users className="h-5 w-5 text-[#FFC857]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#1F3B4D]">{t('landing.contact.salesForm.title')}</h3>
                  </div>
                  <button 
                    onClick={() => {
                      setActiveForm(null);
                      setSalesFormSuccess(false);
                      setSalesFormError('');
                    }}
                    className="text-[#1F3B4D]/70 hover:text-[#1F3B4D] rounded-full p-1"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                  </button>
                </div>
                <div className="p-6">
                  {salesFormSuccess ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                      <div className="text-green-500 mt-0.5">
                        <Check className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-green-800 font-medium">{t('landing.contact.salesForm.submitSuccess') || 'Message sent successfully!'}</p>
                        <p className="text-green-700 text-sm mt-1">{t('landing.contact.salesForm.submitSuccessDetail') || 'Thank you for contacting us. Our team will get back to you shortly.'}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-[#1F3B4D]/70 mb-6">{t('landing.contact.salesForm.description')}</p>
                  )}
                  
                  {salesFormError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                      <div className="text-red-500 mt-0.5">
                        <AlertCircle className="h-5 w-5" />
                      </div>
                      <p className="text-red-800">{salesFormError}</p>
                    </div>
                  )}
                  
                  <form className="space-y-4" onSubmit={handleSalesFormSubmit}>
                    <div>
                      <label htmlFor="modal-name" className="block text-sm font-medium text-[#1F3B4D] mb-2">
                        {t('landing.contact.salesForm.name')}
                      </label>
                      <input
                        type="text"
                        id="modal-name"
                        name="full_name"
                        value={salesFormData.full_name}
                        onChange={handleSalesFormChange}
                        className="w-full px-4 py-3 border border-[#1F3B4D]/20 rounded-lg focus:ring-2 focus:ring-[#4F94D4] focus:border-[#4F94D4] outline-none transition-all"
                        placeholder={t('landing.contact.salesForm.namePlaceholder')}
                        required
                        disabled={salesFormSubmitting || salesFormSuccess}
                      />
                    </div>
                    <div>
                      <label htmlFor="modal-email" className="block text-sm font-medium text-[#1F3B4D] mb-2">
                        {t('landing.contact.salesForm.email')}
                      </label>
                      <input
                        type="email"
                        id="modal-email"
                        name="email"
                        value={salesFormData.email}
                        onChange={handleSalesFormChange}
                        className="w-full px-4 py-3 border border-[#1F3B4D]/20 rounded-lg focus:ring-2 focus:ring-[#4F94D4] focus:border-[#4F94D4] outline-none transition-all"
                        placeholder={t('landing.contact.salesForm.emailPlaceholder')}
                        required
                        disabled={salesFormSubmitting || salesFormSuccess}
                      />
                    </div>
                    <div>
                      <label htmlFor="modal-company" className="block text-sm font-medium text-[#1F3B4D] mb-2">
                        {t('landing.contact.salesForm.company')}
                      </label>
                      <input
                        type="text"
                        id="modal-company"
                        name="company"
                        value={salesFormData.company}
                        onChange={handleSalesFormChange}
                        className="w-full px-4 py-3 border border-[#1F3B4D]/20 rounded-lg focus:ring-2 focus:ring-[#4F94D4] focus:border-[#4F94D4] outline-none transition-all"
                        placeholder={t('landing.contact.salesForm.companyPlaceholder')}
                        required
                        disabled={salesFormSubmitting || salesFormSuccess}
                      />
                    </div>
                    <div>
                      <label htmlFor="modal-employees" className="block text-sm font-medium text-[#1F3B4D] mb-2">
                        {t('landing.contact.salesForm.employees')}
                      </label>
                      <select
                        id="modal-employees"
                        name="number_of_employees"
                        value={salesFormData.number_of_employees}
                        onChange={handleSalesFormChange}
                        className="w-full px-4 py-3 border border-[#1F3B4D]/20 rounded-lg focus:ring-2 focus:ring-[#4F94D4] focus:border-[#4F94D4] outline-none transition-all"
                        required
                        disabled={salesFormSubmitting || salesFormSuccess}
                      >
                        <option value="">{t('landing.contact.salesForm.employeesPlaceholder')}</option>
                        <option value="1-10">1-10</option>
                        <option value="11-50">11-50</option>
                        <option value="51-200">51-200</option>
                        <option value="201-500">201-500</option>
                        <option value="501+">501+</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="modal-message" className="block text-sm font-medium text-[#1F3B4D] mb-2">
                        {t('landing.contact.salesForm.message')}
                      </label>
                      <textarea
                        id="modal-message"
                        name="message"
                        value={salesFormData.message}
                        onChange={handleSalesFormChange}
                        rows={4}
                        className="w-full px-4 py-3 border border-[#1F3B4D]/20 rounded-lg focus:ring-2 focus:ring-[#4F94D4] focus:border-[#4F94D4] outline-none transition-all"
                        placeholder={t('landing.contact.salesForm.messagePlaceholder')}
                        required
                        disabled={salesFormSubmitting || salesFormSuccess}
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      className={`w-full rounded-lg bg-[#FFC857] px-6 py-3 mt-2 text-base font-semibold text-[#1F3B4D] shadow-md hover:bg-[#FFC857]/90 hover:shadow-lg transition-all flex items-center justify-center ${(salesFormSubmitting || salesFormSuccess) ? 'opacity-70 cursor-not-allowed' : ''}`}
                      disabled={salesFormSubmitting || salesFormSuccess}
                    >
                      {salesFormSubmitting ? (
                        <>
                          <span className="w-5 h-5 border-2 border-[#1F3B4D] border-t-transparent rounded-full animate-spin mr-2"></span>
                          {t('landing.contact.salesForm.submitting') || 'Sending...'}
                        </>
                      ) : salesFormSuccess ? (
                        <>
                          {t('landing.contact.salesForm.submitted') || 'Message Sent'}
                          <Check className="w-5 h-5 ml-2" />
                        </>
                      ) : (
                        <>
                          {t('landing.contact.salesForm.submit')}
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </>
            )}

            {/* Demo Form */}
            {activeForm === 'demo' && (
              <>
                <div className="p-6 pb-2 flex items-center justify-between border-b border-[#1F3B4D]/10">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-[#4F94D4]/10">
                      <Calendar className="h-5 w-5 text-[#4F94D4]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#1F3B4D]">{t('landing.contact.demoForm.title')}</h3>
                  </div>
                  <button 
                    onClick={() => {
                      setActiveForm(null);
                      setDemoFormSuccess(false);
                      setDemoFormError('');
                    }}
                    className="text-[#1F3B4D]/70 hover:text-[#1F3B4D] rounded-full p-1"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                  </button>
                </div>
                <div className="p-6">
                  {demoFormSuccess ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                      <div className="text-green-500 mt-0.5">
                        <Check className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-green-800 font-medium">{t('landing.contact.demoForm.submitSuccess') || 'Demo scheduled successfully!'}</p>
                        <p className="text-green-700 text-sm mt-1">{t('landing.contact.demoForm.submitSuccessDetail') || 'Thank you for scheduling a demo. We will contact you shortly to confirm the details.'}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-[#1F3B4D]/70 mb-6">{t('landing.contact.demoForm.description')}</p>
                  )}
                  
                  {demoFormError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                      <div className="text-red-500 mt-0.5">
                        <AlertCircle className="h-5 w-5" />
                      </div>
                      <p className="text-red-800">{demoFormError}</p>
                    </div>
                  )}
                  
                  <form className="space-y-4" onSubmit={handleDemoFormSubmit}>
                    <div>
                      <label htmlFor="modal-demo-name" className="block text-sm font-medium text-[#1F3B4D] mb-2">
                        {t('landing.contact.demoForm.name')}
                      </label>
                      <input
                        type="text"
                        id="modal-demo-name"
                        name="full_name"
                        value={demoFormData.full_name}
                        onChange={handleDemoFormChange}
                        className="w-full px-4 py-3 border border-[#1F3B4D]/20 rounded-lg focus:ring-2 focus:ring-[#4F94D4] focus:border-[#4F94D4] outline-none transition-all"
                        placeholder={t('landing.contact.demoForm.namePlaceholder')}
                        required
                        disabled={demoFormSubmitting || demoFormSuccess}
                      />
                    </div>
                    <div>
                      <label htmlFor="modal-demo-email" className="block text-sm font-medium text-[#1F3B4D] mb-2">
                        {t('landing.contact.demoForm.email')}
                      </label>
                      <input
                        type="email"
                        id="modal-demo-email"
                        name="email"
                        value={demoFormData.email}
                        onChange={handleDemoFormChange}
                        className="w-full px-4 py-3 border border-[#1F3B4D]/20 rounded-lg focus:ring-2 focus:ring-[#4F94D4] focus:border-[#4F94D4] outline-none transition-all"
                        placeholder={t('landing.contact.demoForm.emailPlaceholder')}
                        required
                        disabled={demoFormSubmitting || demoFormSuccess}
                      />
                    </div>
                    <div>
                      <label htmlFor="modal-demo-company" className="block text-sm font-medium text-[#1F3B4D] mb-2">
                        {t('landing.contact.demoForm.company')}
                      </label>
                      <input
                        type="text"
                        id="modal-demo-company"
                        name="company"
                        value={demoFormData.company}
                        onChange={handleDemoFormChange}
                        className="w-full px-4 py-3 border border-[#1F3B4D]/20 rounded-lg focus:ring-2 focus:ring-[#4F94D4] focus:border-[#4F94D4] outline-none transition-all"
                        placeholder={t('landing.contact.demoForm.companyPlaceholder')}
                        required
                        disabled={demoFormSubmitting || demoFormSuccess}
                      />
                    </div>
                    <div>
                      <label htmlFor="modal-position" className="block text-sm font-medium text-[#1F3B4D] mb-2">
                        {t('landing.contact.demoForm.position')}
                      </label>
                      <input
                        type="text"
                        id="modal-position"
                        name="position"
                        value={demoFormData.position}
                        onChange={handleDemoFormChange}
                        className="w-full px-4 py-3 border border-[#1F3B4D]/20 rounded-lg focus:ring-2 focus:ring-[#4F94D4] focus:border-[#4F94D4] outline-none transition-all"
                        placeholder={t('landing.contact.demoForm.positionPlaceholder')}
                        required
                        disabled={demoFormSubmitting || demoFormSuccess}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="modal-preferred-date" className="block text-sm font-medium text-[#1F3B4D] mb-2">
                          {t('landing.contact.demoForm.preferredDate')}
                        </label>
                        <input
                          type="date"
                          id="modal-preferred-date"
                          name="preferred_date"
                          value={demoFormData.preferred_date}
                          onChange={handleDemoFormChange}
                          className="w-full px-4 py-3 border border-[#1F3B4D]/20 rounded-lg focus:ring-2 focus:ring-[#4F94D4] focus:border-[#4F94D4] outline-none transition-all"
                          required
                          disabled={demoFormSubmitting || demoFormSuccess}
                          min={new Date().toISOString().split('T')[0]} // Set minimum date to today
                        />
                      </div>
                      <div>
                        <label htmlFor="modal-preferred-time" className="block text-sm font-medium text-[#1F3B4D] mb-2">
                          {t('landing.contact.demoForm.preferredTime')}
                        </label>
                        <select
                          id="modal-preferred-time"
                          name="preferred_time"
                          value={demoFormData.preferred_time}
                          onChange={handleDemoFormChange}
                          className="w-full px-4 py-3 border border-[#1F3B4D]/20 rounded-lg focus:ring-2 focus:ring-[#4F94D4] focus:border-[#4F94D4] outline-none transition-all"
                          required
                          disabled={demoFormSubmitting || demoFormSuccess}
                        >
                          <option value="">{t('landing.contact.demoForm.preferredTimePlaceholder')}</option>
                          <option value="9am-12pm">{t('landing.contact.demoForm.morning')}</option>
                          <option value="12pm-5pm">{t('landing.contact.demoForm.afternoon')}</option>
                          <option value="5pm-8pm">{t('landing.contact.demoForm.evening')}</option>
                        </select>
                      </div>
                    </div>
                    <button
                      type="submit"
                      className={`w-full rounded-lg bg-[#4F94D4] px-6 py-3 mt-2 text-base font-semibold text-white shadow-md hover:bg-[#4F94D4]/90 hover:shadow-lg transition-all flex items-center justify-center ${(demoFormSubmitting || demoFormSuccess) ? 'opacity-70 cursor-not-allowed' : ''}`}
                      disabled={demoFormSubmitting || demoFormSuccess}
                    >
                      {demoFormSubmitting ? (
                        <>
                          <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                          {t('landing.contact.demoForm.submitting') || 'Scheduling...'}
                        </>
                      ) : demoFormSuccess ? (
                        <>
                          {t('landing.contact.demoForm.submitted') || 'Demo Scheduled'}
                          <Check className="w-5 h-5 ml-2" />
                        </>
                      ) : (
                        <>
                          {t('landing.contact.demoForm.submit')}
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing;